import React, { useState } from "react";
import {
  CheckCircle2,
  ChevronDown,
  FileText,
  Mail,
  Scale,
  Sparkles,
} from "lucide-react";
import * as LucideIcons from "lucide-react";
import {
  ARTICLE_PROSE_CLASS,
  getSectionNavLabel,
  sanitizeArticleHtml,
} from "../../utils/consumerArticleHelpers";

function resolveIcon(name = "file-text") {
  const pascal = String(name)
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join("");
  return LucideIcons[pascal] || FileText;
}

function TakeawaysSection({ section, color }) {
  const data = section.data || {};
  const items = Array.isArray(data.items) ? data.items : [];

  return (
    <div className="rounded-3xl border border-[#FFD221]/30 bg-[#FFD221]/10 p-6 sm:p-7">
      {data.title ? (
        <p className="mb-4 text-xs font-black uppercase tracking-[0.2em] text-[#9a7b00]">{data.title}</p>
      ) : (
        <p className="mb-4 text-xs font-black uppercase tracking-[0.2em] text-[#9a7b00]">Key takeaways</p>
      )}
      <div className="grid gap-3 md:grid-cols-3">
        {items.map((item) => {
          const Icon = resolveIcon(item.icon);
          return (
            <div key={item.id || item.title} className="rounded-2xl bg-white p-4 shadow-sm">
              <div
                className="mb-3 inline-flex h-9 w-9 items-center justify-center rounded-xl"
                style={{ background: `${color}15`, color }}
              >
                <Icon size={17} strokeWidth={2.25} />
              </div>
              <p className="text-sm font-black text-gray-900">{item.title}</p>
              {item.description ? (
                <p className="mt-2 text-sm leading-relaxed text-gray-600">{item.description}</p>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TextSection({ section, color }) {
  const data = section.data || {};
  const layout = data.layout || "left-content";

  return (
    <div className={layout === "left-content" ? "" : "text-center"}>
      {data.title ? (
        <h2 className="text-2xl font-black text-gray-950 sm:text-3xl">{data.title}</h2>
      ) : null}
      {data.content ? (
        <div
          className={`${ARTICLE_PROSE_CLASS} ${data.title ? "mt-4" : ""}`}
          dangerouslySetInnerHTML={{ __html: sanitizeArticleHtml(data.content) }}
        />
      ) : null}
      {data.bullets?.length ? (
        <ul className={`grid gap-3 ${data.content || data.title ? "mt-5" : ""}`}>
          {data.bullets.map((item) => (
            <li key={item} className="flex items-start gap-3 rounded-2xl bg-[#f7f4ec] p-4 text-sm text-gray-700">
              <CheckCircle2 size={17} className="mt-0.5 shrink-0" style={{ color }} />
              {item}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function QuoteSection({ section, color }) {
  const data = section.data || {};
  return (
    <blockquote
      className="rounded-[2rem] border-l-4 bg-gray-950 p-7 text-white sm:p-8"
      style={{ borderColor: color }}
    >
      <p className="text-xl font-black leading-snug sm:text-2xl">&ldquo;{data.quote}&rdquo;</p>
      {data.author ? <p className="mt-4 text-sm text-white/50">— {data.author}</p> : null}
    </blockquote>
  );
}

function RichTextSection({ section }) {
  const data = section.data || {};
  return (
    <div>
      {data.title ? <h2 className="text-2xl font-black text-gray-950 sm:text-3xl">{data.title}</h2> : null}
      {data.content ? (
        <div
          className={`${ARTICLE_PROSE_CLASS} ${data.title ? "mt-4" : ""}`}
          dangerouslySetInnerHTML={{ __html: sanitizeArticleHtml(data.content) }}
        />
      ) : null}
    </div>
  );
}

function ChecklistSection({ section, color }) {
  const data = section.data || {};
  const items = Array.isArray(data.items) ? data.items : [];

  return (
    <div>
      {data.title ? <h2 className="text-2xl font-black text-gray-950 sm:text-3xl">{data.title}</h2> : null}
      <ul className={`space-y-3 ${data.title ? "mt-5" : ""}`}>
        {items.map((item, idx) => {
          const checked = item.checked === true || item.checked === 1 || item.checked === "1";
          return (
            <li
              key={item.id || item.label || idx}
              className="flex items-start gap-3 rounded-2xl border border-black/[0.06] bg-[#f7f4ec] p-4 text-sm text-gray-700"
            >
              <CheckCircle2
                size={18}
                className="mt-0.5 shrink-0"
                style={{ color: checked ? color : "#cbd5e1" }}
              />
              <span className={checked ? "font-semibold" : ""}>{item.label}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function FaqItem({ item, color }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="overflow-hidden rounded-2xl border border-black/[0.08] bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
        aria-expanded={open}
      >
        <span className="text-sm font-black text-gray-900 sm:text-base">{item.question}</span>
        <ChevronDown
          size={18}
          className={`shrink-0 text-gray-400 transition ${open ? "rotate-180" : ""}`}
          style={{ color: open ? color : undefined }}
        />
      </button>
      {open ? (
        <div className="border-t border-black/[0.06] px-5 pb-4 pt-3 text-sm leading-relaxed text-gray-600">
          {item.answer}
        </div>
      ) : null}
    </div>
  );
}

function FaqSection({ section, color }) {
  const data = section.data || {};
  const items = Array.isArray(data.items) ? data.items : [];

  return (
    <div>
      {data.title ? <h2 className="text-2xl font-black text-gray-950 sm:text-3xl">{data.title}</h2> : null}
      <div className={`space-y-2 ${data.title ? "mt-5" : ""}`}>
        {items.map((item, idx) => (
          <FaqItem key={item.id || item.question || idx} item={item} color={color} />
        ))}
      </div>
    </div>
  );
}

function HeroInlineSection({ section, color }) {
  const data = section.data || {};
  const coverUrl = data.cover_image?.url;

  if (!coverUrl) return null;

  return (
    <div className="overflow-hidden rounded-[2rem] bg-black text-white">
      <div className="relative min-h-[16rem]">
        <img src={coverUrl} alt={data.cover_image?.alt || ""} className="absolute inset-0 h-full w-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/35 to-transparent" />
        <div className="relative flex min-h-[16rem] flex-col justify-end p-7">
          {data.badge ? (
            <p className="text-xs font-black uppercase tracking-[0.2em]" style={{ color }}>
              {data.badge}
            </p>
          ) : null}
          {data.subtitle ? <p className="mt-2 max-w-xl text-sm text-white/70">{data.subtitle}</p> : null}
        </div>
      </div>
    </div>
  );
}

export function ArticleSectionBlock({ section, color }) {
  if (!section?.type) return null;

  switch (section.type) {
    case "hero":
      return <HeroInlineSection section={section} color={color} />;
    case "takeaways":
      return <TakeawaysSection section={section} color={color} />;
    case "text_section":
      return <TextSection section={section} color={color} />;
    case "quote":
      return <QuoteSection section={section} color={color} />;
    case "rich_text":
      return <RichTextSection section={section} />;
    case "checklist":
      return <ChecklistSection section={section} color={color} />;
    case "faq":
      return <FaqSection section={section} color={color} />;
    default:
      return (
        <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-5 text-sm text-gray-500">
          <Sparkles size={16} className="mb-2 inline" style={{ color }} /> Unsupported section: {section.type}
        </div>
      );
  }
}

export function ArticleSectionsList({ sections, color, className = "" }) {
  if (!sections?.length) return null;

  return (
    <div className={`space-y-10 ${className}`}>
      {sections.map((section) => (
        <section
          key={section.id || `${section.type}-${section.data?.title || ""}`}
          id={section.id}
          className="scroll-mt-28"
          aria-labelledby={section.id ? `${section.id}-label` : undefined}
        >
          <span id={section.id ? `${section.id}-label` : undefined} className="sr-only">
            {getSectionNavLabel(section)}
          </span>
          <ArticleSectionBlock section={section} color={color} />
        </section>
      ))}
    </div>
  );
}

export { Mail, Scale };
